import { directionExposureLabels } from '@/utils/capitalRuleFormatters';
import React from 'react';
import Card from '@/components/UI/Card';
import Button from '@/components/UI/Button';
import { DeleteButton, EditButton, EmptyState } from '@/components/UI';
import type { CategoryPositionRule } from '@einhaus/project314-types';

interface CategoryRulesCardProps {
    rules: CategoryPositionRule[];
    onDeleteRule: (ruleId: string, type: 'directionMax' | 'categoryLimit' | 'positionSizeRule') => void;
    setEditingRuleId: React.Dispatch<React.SetStateAction<string | null>>;
    setNewRule: React.Dispatch<React.SetStateAction<Partial<CategoryPositionRule>>>;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const CategoryRulesCard: React.FC<CategoryRulesCardProps> = ({ rules, onDeleteRule, setEditingRuleId, setNewRule, setShowModal }) => (
    <Card className="mb-4" headerClassName="flex justify-between items-center" title="Category Position Limit Rules">
        <Button
            variant="link"
            onClick={() => {
                setEditingRuleId(null);

                setNewRule({
                    categoryLimitDirection: 'either',
                    categoryLimitType: 'exactCategoriesMatch',
                    categoryLimitMaximumType: 'positions',
                    value: 0
                });

                setShowModal(true);
            }}
        >
            + Add
        </Button>

        {rules.length === 0 ? (
            <EmptyState size="sm" title="No category position limit rules configured." />
        ) : (
            <div className="overflow-x-auto">
                <table className="min-w-full bg-zinc-800 text-white border-collapse">
                    <thead>
                        <tr className="bg-zinc-700">
                            <th className="px-4 py-2 text-left">Underlying exposure</th>
                            <th className="px-4 py-2 text-left">Limit Type</th>
                            <th className="px-4 py-2 text-left">Maximum Type</th>
                            <th className="px-4 py-2 text-left">Maximum Value</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map((rule, index) => (
                            <tr className={index % 2 === 0 ? 'bg-zinc-800' : 'bg-zinc-750'} key={rule.id}>
                                <td className="px-4 py-2 border-t border-zinc-700">
                                    {directionExposureLabels[rule.categoryLimitDirection]}
                                </td>
                                <td className="px-4 py-2 border-t border-zinc-700">{rule.categoryLimitType}</td>
                                <td className="px-4 py-2 border-t border-zinc-700">{rule.categoryLimitMaximumType}</td>
                                <td className="px-4 py-2 border-t border-zinc-700">{rule.value}</td>
                                <td className="px-4 py-2 border-t border-zinc-700">
                                    <div className="flex items-center gap-2">
                                        <EditButton
                                            size="sm"
                                            onClick={() => {
                                                setEditingRuleId(rule.id);

                                                setNewRule({
                                                    categoryLimitDirection: rule.categoryLimitDirection,
                                                    categoryLimitType: rule.categoryLimitType,
                                                    categoryLimitMaximumType: rule.categoryLimitMaximumType,
                                                    value: rule.value
                                                });

                                                setShowModal(true);
                                            }}
                                        />
                                        <DeleteButton size="sm" onClick={() => onDeleteRule(rule.id, 'categoryLimit')} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </Card>
);

export default CategoryRulesCard;
